import { useState, useCallback } from "react";
import {
  Star,
  ChevronDown,
  ChevronRight,
  User,
  Bot,
  Code,
  Image,
  FileText,
  MessageSquare,
  Copy,
  Check,
  Maximize2,
} from "lucide-react";
import type { KeepsakedMessage } from "../hooks/useKeepsakes";

interface KeepsakesListProps {
  keepsakes: KeepsakedMessage[];
  onSelect: (messageId: string, sessionId: string) => void;
  /** Opens the full cross-session, cross-identity keepsakes gallery. When
   *  omitted, no "view all" affordance is rendered (this panel still works
   *  standalone). */
  onOpenGallery?: () => void;
}

// Exported for reuse by KeepsakesGallery.tsx — same preview/formatting rules
// apply to a keepsake wherever it's rendered.
export function relativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}

// Intentional preview variant — keeps [code] placeholder + collapses whitespace. See src/shared/strip-markdown.ts for canonical.
export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, "[code]")
    .replace(/`[^`]+`/g, (m) => m.slice(1, -1))
    .replace(/!\[.*?\]\(.*?\)/g, "[image]")
    .replace(/\[.*?\]\(.*?\)/g, (m) => {
      const label = m.match(/\[(.*?)\]/)?.[1] ?? "";
      return label;
    })
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

export function contentTypeIcon(content: string) {
  if (/```/.test(content)) return <Code size={13} />;
  if (/data:image\/|!\[/.test(content)) return <Image size={13} />;
  if (content.length > 200) return <FileText size={13} />;
  return <MessageSquare size={13} />;
}

export function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(content).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    },
    [content],
  );

  return (
    <button
      className="keepsake-card__copy"
      onClick={handleCopy}
      title="Copy to clipboard"
      aria-label="Copy message content"
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

function KeepsakeCard({
  k,
  onSelect,
}: {
  k: KeepsakedMessage;
  onSelect: (id: string, sessionId: string) => void;
}) {
  const preview = truncate(stripMarkdown(k.content), 120);
  const sessionLabel = k.session_label ?? "Untitled";

  return (
    <div className="keepsake-card" role="article">
      <CopyButton content={k.content} />
      <button
        className="keepsake-card__body"
        onClick={() => onSelect(k.id, k.session_id)}
        aria-label={`Go to keepsake: ${preview}`}
      >
        <div className="keepsake-card__header">
          <span className="keepsake-card__type-icon">
            {contentTypeIcon(k.content)}
          </span>
          <span className="keepsake-card__role">
            {k.role === "user" ? <User size={11} /> : <Bot size={11} />}
          </span>
        </div>
        <p className="keepsake-card__preview">{preview}</p>
        <div className="keepsake-card__meta">
          <span className="keepsake-card__session-badge">{sessionLabel}</span>
          <span className="keepsake-card__time">{relativeTime(k.created_at)}</span>
        </div>
      </button>
    </div>
  );
}

export function KeepsakesList({ keepsakes, onSelect, onOpenGallery }: KeepsakesListProps) {
  // Default collapsed so the panel doesn't crowd the active chat list —
  // it shows just the "Keepsakes (N)" header; the user expands it on demand.
  const [collapsed, setCollapsed] = useState(true);

  if (keepsakes.length === 0) {
    return (
      <div className="keepsakes-panel">
        <div className="keepsakes-empty">
          <Star size={12} />
          <span>Star messages to save them here</span>
        </div>
      </div>
    );
  }

  const useGrid = keepsakes.length >= 3;

  return (
    <div className="keepsakes-panel">
      <button
        className="keepsakes-header"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <Star size={14} />
        <span>Keepsakes</span>
        <span className="keepsakes-count">{keepsakes.length}</span>
        {onOpenGallery && (
          <span
            className="keepsakes-header__gallery-btn"
            role="button"
            tabIndex={0}
            title="View all keepsakes"
            aria-label="Open full keepsakes gallery"
            onClick={(e) => {
              e.stopPropagation();
              onOpenGallery();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onOpenGallery();
              }
            }}
          >
            <Maximize2 size={12} />
          </span>
        )}
      </button>
      {!collapsed && (
        <div className={useGrid ? "keepsakes-grid" : "keepsakes-list"}>
          {keepsakes.map((k) => (
            <KeepsakeCard key={k.id} k={k} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}
